import { SectionTemplates } from "../helpers/enums";

export const initializeStory = (story) => ({...story, sections: []});

export const insertSection = (story, section, insertIndex) => {
    const sections = [...(story.sections || [])];
    const total = sections.length;
    const targetIndex =
        typeof insertIndex === "number" && insertIndex >= 0 && insertIndex <= total
            ? insertIndex
            : total;
    sections.splice(targetIndex, 0, section);
    return {
        ...story,
        sections: sections.map((s, i) => ({ ...s, order: i + 1 })),
    };
};

export const addSection = (story, section, insertIndex) => {
    if (insertIndex !== undefined) {
        return insertSection(story, section, insertIndex);
    }
    return {
        ...story,
        sections: [...(story.sections || []), section],
    };
};
export const deleteSection = (story, sectionId) => ({
    ...story,
    sections: story.sections
        .filter((s, i) => sectionId !== i)
        .map((s, i) => ({...s, order: i + 1})),
});
export const updateSection = (story, sectionId, section) => {
    const exists = story.sections[sectionId];
    if (exists) {
        return {
            ...story,
            sections: story.sections.map((s, i) =>
                i !== sectionId ? s : {...s, ...section}
            ),
        };
    } else {
        throw new Error(`Section with sectionId: ${sectionId} does not exist`);
    }
};
export const updateSectionOrder = (story, fromIndex, toIndex) => {
    const move = (from, to, array) => {
        const thisArray = [...array];
        thisArray.splice(to, 0, thisArray.splice(from, 1)[0]);
        return thisArray;
    };
    const sortedSections = [...(story.sections || [])].sort((a, b) => a.order - b.order);
    const updatedSections = move(fromIndex, toIndex, sortedSections).map(
        (s, i) => ({...s, order: i + 1})
    );
    return {...story, sections: updatedSections};
};

const generateRandomId = () => Math.random().toString(36).substring(2, 11);

export const addContainer = (story, container = {}, sectionId) => ({
    ...story,
    sections: story.sections.map((s, i) => {
        if (i !== sectionId) return s;

        let containerToAdd = container;
        if (s.templateId === SectionTemplates.ZIGZAG_SECTION) {
            const count = s.containers?.length || 0;
            const isEven = count % 2 === 0;
            const startColumn = isEven ? 2 : 6;
            const endColumn = isEven ? 8 : 12;
            const blocks =
                container?.blocks && container.blocks.length > 0
                    ? container.blocks
                    : [
                          { type: "image" },
                          { type: "text", placeholder: "Klikk her for å skrive" },
                      ];
            containerToAdd = {
                ...container,
                startColumn,
                endColumn,
                blocks,
            };
        } else if (
            s.templateId === SectionTemplates.SCROLL_MAP ||
            s.type === "stickyMap"
        ) {
            const blocks =
                container?.blocks && container.blocks.length > 0
                    ? container.blocks
                    : [
                          {
                              id: generateRandomId(),
                              type: "text",
                              placeholder: "Klikk her for å skrive",
                          },
                      ];
            containerToAdd = {
                ...container,
                backgroundColor:
                    container.backgroundColor ||
                    s.containers?.[0]?.backgroundColor ||
                    "#ffffffb3",
                borderRadius:
                    container.borderRadius !== undefined
                        ? container.borderRadius
                        : true,
                startColumn:
                    container.startColumn ??
                    s.containers?.[0]?.startColumn ??
                    2,
                endColumn:
                    container.endColumn ??
                    s.containers?.[0]?.endColumn ??
                    6,
                horizontalAlign: container.horizontalAlign || "center",
                stepPosition: container.stepPosition || "bottom",
                removable: true,
                blocks,
            };
        } else if (
            s.templateId === SectionTemplates.STICKY_SECTION ||
            s.templateId === SectionTemplates.STICKY_SECTION_MULTI ||
            s.type === "sticky"
        ) {
            containerToAdd = {
                ...container,
                backgroundColor:
                    container.backgroundColor ||
                    s.containers?.[0]?.backgroundColor ||
                    "#ffffffb3",
                borderRadius:
                    container.borderRadius !== undefined
                        ? container.borderRadius
                        : true,
                startColumn:
                    container.startColumn ??
                    s.containers?.[0]?.startColumn ??
                    5,
                endColumn:
                    container.endColumn ??
                    s.containers?.[0]?.endColumn ??
                    6,
                horizontalAlign: container.horizontalAlign || "center",
                stepPosition: container.stepPosition || "bottom",
                removable: true,
            };
        }

        const newBg = Array.isArray(s.backgroundImage)
            ? [...s.backgroundImage, undefined]
            : s.backgroundImage;

        return {
            ...s,
            ...(Array.isArray(s.backgroundImage) ? { backgroundImage: newBg } : {}),
            containers: [...(s.containers || []), containerToAdd],
        };
    }),
});
export const updateContainer = (story, sectionId, containerId, container) => {
    const exists =
        story.sections[sectionId]?.containers[containerId] !== undefined;
    if (exists) {
        return {
            ...story,
            sections: story.sections.map((s, i) =>
                i !== sectionId
                    ? s
                    : {
                        ...s,
                        containers: s.containers.map((c, j) => {
                            if (j !== containerId) {
                                return c;
                            }
                            if (container === undefined) {
                                return {};
                            }
                            return {...c, ...container};
                        }),
                    }
            ),
        };
    } else {
        throw new Error(
            `Block with sectionId: ${sectionId}, containerId: ${containerId} does not exist`
        );
    }
};
export const removeContainer = (story, sectionId, containerId) => {
    const exists =
        story.sections[sectionId]?.containers[containerId] !== undefined;
    if (exists) {
        return {
            ...story,
            sections: story.sections.map((s, i) => {
                if (i !== sectionId) return s;
                const newBg = Array.isArray(s.backgroundImage)
                    ? s.backgroundImage.filter((_, j) => j !== containerId)
                    : s.backgroundImage;
                return {
                    ...s,
                    ...(Array.isArray(s.backgroundImage) ? { backgroundImage: newBg } : {}),
                    containers:
                        s.containers.length === 1
                            ? []
                            : [...s.containers.filter((c, j) => j !== containerId)],
                };
            }),
        };
    } else {
        throw new Error(
            `container with sectionId: ${sectionId}, containerId: ${containerId}, does not exist`
        );
    }
};

export const addBlock = (story, sectionId, containerId, block) => {
    const exists =
        story.sections[sectionId]?.containers[containerId] !== undefined;
    if (exists) {
        return {
            ...story,
            sections: story.sections.map((s, i) =>
                i !== sectionId
                    ? s
                    : {
                        ...s,
                        containers: s.containers.map((c, j) =>
                            j !== containerId
                                ? c
                                : {
                                    ...c,
                                    blocks: [...c.blocks, {...block}],
                                }
                        ),
                    }
            ),
        };
    } else {
        throw new Error(
            `Block with sectionId: ${sectionId}, containerId: ${containerId} does not exist`
        );
    }
};


export const updateBlock = (story, sectionId, containerId, blockId, block) => {
    const exists =
        story.sections[sectionId]?.containers[containerId]?.blocks[blockId] !==
        undefined;
    if (exists) {
        return {
            ...story,
            sections: story.sections.map((s, i) =>
                i !== sectionId
                    ? s
                    : {
                        ...s,
                        containers: s.containers.map((c, j) =>
                            j !== containerId
                                ? c
                                : {
                                    ...c,
                                    blocks: [
                                        ...c.blocks.map((b, k) => {
                                            if (k !== blockId) {
                                                return b;
                                            }
                                            if (block === undefined) {
                                                return {};
                                            }
                                            return {...b, ...block};
                                        }),
                                    ],
                                }
                        ),
                    }
            ),
        };
    } else {
        throw new Error(
            `Block with sectionId: ${sectionId}, containerId: ${containerId}, blockId: ${blockId}, does not exist`
        );
    }
};
export const removeBlock = (story, sectionId, containerId, blockId) => {
    const exists =
        story.sections[sectionId]?.containers[containerId]?.blocks[blockId] !==
        undefined;
    if (exists) {
        return {
            ...story,
            sections: story.sections.map((section, i) =>
                i !== sectionId
                    ? section
                    : {
                        ...section,
                        containers: section.containers.map((container, j) =>
                            j !== containerId
                                ? container
                                : {
                                    ...container,
                                    blocks: container.blocks.filter(
                                        (block, k) => k !== blockId
                                    ),
                                }
                        ),
                    }
            ),
        };
    } else {
        throw new Error(
            `Block with sectionId: ${sectionId}, containerId: ${containerId}, blockId: ${blockId}, does not exist`
        );
    }
};
